import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scissors, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantLoginPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/tenants/by-slug/${slug}`)
      .then(res => setTenant(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const signIn = async () => {
    setSubmitting(true);
    try {
      // Accept email OR phone in the same field; backend auto-detects.
      const id = (email || "").trim();
      const payload = id.includes("@") ? { email: id } : { phone: id };
      const res = await axios.post(`${API}/auth/login`, { ...payload, password });
      const userTenant = res.data.user?.tenant_id;
      if (userTenant && tenant && userTenant !== tenant.tenant_id) {
        toast.error("This account belongs to a different workspace. Please use the correct URL.");
        setSubmitting(false);
        return;
      }
      localStorage.setItem("salonflow_token", res.data.token);
      login(res.data.user, res.data.token);
      if (res.data.user?.role === "platform_admin") {
        navigate("/platform");
      } else {
        navigate(`/dashboard`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-in failed");
    }
    setSubmitting(false);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 flex items-center justify-center p-6" data-testid="tenant-not-found">
        <div className="text-center text-white">
          <p className="font-['Outfit'] text-2xl font-semibold mb-2">Workspace not found</p>
          <p className="text-white/40 text-sm mb-5">No salon is registered at <code className="font-mono bg-white/10 px-1.5 py-0.5 rounded">/{slug}</code></p>
          <Link to="/signup"><Button className="rounded-full h-11 px-6 bg-white text-slate-900 hover:bg-slate-100">Create your workspace</Button></Link>
        </div>
      </div>
    );
  }

  if (!tenant) return (
    <div className="flex items-center justify-center h-screen bg-slate-950"><Loader2 className="w-6 h-6 text-rose-400 animate-spin" /></div>
  );

  const accent = tenant.branding?.primary_color || "#F43F5E";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-6" data-testid="tenant-login-page">
      <div className="w-full max-w-md bg-white rounded-[28px] shadow-xl border border-slate-100 p-8 sm:p-10">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
            <Scissors className="w-6 h-6" strokeWidth={2} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Sign in to</p>
          <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900 mt-0.5" data-testid="tenant-login-business-name">
            {tenant.business_name}
          </h1>
          <p className="text-xs text-slate-400 font-mono mt-1">app.com/t/{tenant.slug}</p>
        </div>

        <div className="space-y-3">
          <Input data-testid="tenant-login-email" type="text" value={email}
            onChange={e => setEmail(e.target.value)}
            inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username"
            placeholder="Email or phone number" className="h-12 rounded-xl border-slate-200 text-base" />
          <Input data-testid="tenant-login-password" type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="current-password"
            onKeyDown={e => e.key === "Enter" && signIn()}
            placeholder="Password" className="h-12 rounded-xl border-slate-200 text-base" />
          <Button onClick={signIn} disabled={submitting || !email || !password}
            data-testid="tenant-login-submit"
            className="w-full h-12 rounded-full text-white font-semibold text-base"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }}>
            {submitting ? "Signing in…" : "Sign In"}
          </Button>
        </div>

        <p className="text-center text-slate-400 text-xs mt-6 font-['Manrope']">
          Wrong workspace? <Link to="/signup" className="text-slate-700 hover:underline">Create a new one</Link>
        </p>
      </div>
    </div>
  );
}
